// src/components/pages/admin-dash/LoggingAdmin.jsx
import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import Spinner from "../../ui/Spinner";

/**
 * Admin view for LoggingResource (/admin/logging/level) — change (and,
 * since the GET was added alongside this page, read) a logger's level
 * at runtime without a redeploy (1-ADMIN_ROADMAP.md item 2.3).
 *
 * Leave "Logger name" blank to target the root logger — that's the
 * common case (e.g. bumping everything to DEBUG to chase an
 * intermittent bug), per LoggingResource's own default-to-root behavior.
 */
const LEVELS = ["DEBUG", "INFO", "WARN", "ERROR"];

const LEVEL_TONE = {
  DEBUG: "neutral",
  INFO: "teal",
  WARN: "gold",
  ERROR: "red",
};

export default function LoggingAdmin({ onShowToast }) {
  const [loggerName, setLoggerName] = useState("");
  const [currentLevel, setCurrentLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("INFO");
  const [applying, setApplying] = useState(false);

  const fetchLevel = useCallback(async (name) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/logging/level", { params: name ? { logger: name } : {} });
      const level = res?.data?.level || "INFO";
      setCurrentLevel(level);
      setSelected(level);
    } catch {
      onShowToast?.("Couldn't load current log level.", "error");
    } finally {
      setLoading(false);
    }
  }, [onShowToast]);

  useEffect(() => { fetchLevel(""); }, [fetchLevel]);

  const handleApply = async () => {
    setApplying(true);
    try {
      await axiosInstance.post("/admin/logging/level", {
        logger: loggerName.trim() || null,
        level: selected,
      });
      onShowToast?.(
        `Log level for ${loggerName.trim() || "ROOT"} set to ${selected}.`,
        "success"
      );
      fetchLevel(loggerName.trim());
    } catch (err) {
      onShowToast?.(err?.response?.data?.message || "Failed to change log level.", "error");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Logging
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Change log verbosity at runtime — no redeploy needed. Useful for
          chasing an intermittent bug in production, then reverting.
        </p>
      </header>

      <Card className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <Field
            label="Logger name"
            value={loggerName}
            onChange={(e) => setLoggerName(e.target.value)}
            placeholder="Leave blank for ROOT, or e.g. io.lotto.ai.service"
            className="!mb-0"
          />
          <div className="flex items-end">
            <Button icon="search" size="sm" variant="ghost" onClick={() => fetchLevel(loggerName.trim())} loading={loading}>
              Check current
            </Button>
          </div>
        </div>

        <div>
          <div className="lg-label mb-1.5">Current level</div>
          {loading ? (
            <Spinner size={14} />
          ) : (
            <Badge variant={LEVEL_TONE[currentLevel] || "neutral"}>{currentLevel || "—"}</Badge>
          )}
        </div>

        <div>
          <label className="block text-[12px] font-medium mb-1.5 tracking-wide" style={{ color: "var(--txt)" }}>
            Set level to
          </label>
          <div className="flex flex-wrap gap-2">
            {LEVELS.map((l) => (
              <LevelChip key={l} label={l} active={selected === l} onClick={() => setSelected(l)} />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <Button icon="check" size="sm" onClick={handleApply} loading={applying}>
            Apply
          </Button>
        </div>
      </Card>
    </div>
  );
}

function LevelChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-colors"
      style={{
        background: active ? "var(--gold-dim)" : "var(--surf-2)",
        color:      active ? "var(--gold)"     : "var(--txt-2)",
        border: `1px solid ${active ? "var(--gold-border)" : "var(--border-2)"}`,
      }}
    >
      {active && <Icon name="check" size={11} />}
      {label}
    </button>
  );
}
